import React from 'react';
// Reuso temporal del contenido v1 (`ListSimpleDropdown`). La migración de este
// contenido a `src/ui/` con a11y de menú (roles/teclado/type-ahead) es la Fase 3
// (TAN-9065); hasta entonces el disparador es accesible pero el contenido del
// panel aún NO es navegable por teclado.
import ListSimpleDropdown from '../../../core/modules/components/dropdowns/components/ListSimpleDropdown/ListSimpleDropdown';
import Button from '../../atoms/Button/Button';
import Popover from '../Popover/Popover';
import type { ActionButtonDropdownMenuProps } from './types';
import useActionButtonDropdownMenu from './useActionButtonDropdownMenu';

/**
 * ActionButtonDropdownMenu — v2 molecule (antes `ButtonFixedDropdown`).
 *
 * Menú de acciones con el disparador de botón (icono) soldado dentro. Compone
 * `Button` (disparador) + `Popover` (panel flotante v2) + `ListSimpleDropdown`
 * (contenido v1 reusado) o un `customDropdown` libre. La API replica la del v1
 * para que la migración de consumidores sea drop-in.
 *
 * A11y (esta fase): el disparador expone `aria-haspopup` (`dialog` con
 * `customDropdown`, `menu` con `options`) y `aria-expanded`, y el `Popover`
 * gestiona Escape/click-away y la devolución de foco. La navegación por teclado
 * del contenido del menú llega en la Fase 3 (TAN-9065). `ariaLabel` es
 * obligatorio: es el nombre accesible del disparador icon-only (WCAG 4.1.2).
 */
function ActionButtonDropdownMenu({
  ariaLabel,
  classNameDropdown = '',
  closeOnClickInOption = true,
  customDropdown,
  emitClickEventOpen = () => {},
  fixed = true,
  icon = 'add',
  iconClose,
  iconColor = 'primary',
  iconSize,
  onChangeOpen = () => {},
  open,
  options = [],
  placementDropdown,
  rotate = true,
  size = 'l',
}: Readonly<ActionButtonDropdownMenuProps>): JSX.Element {
  const {
    anchorEl,
    ariaHasPopup,
    buttonClassName,
    containerButtonRef,
    iconButton,
    isOpen,
    onClickInOption,
    onCloseDropdown,
    onToggleDropdown,
    popoverClassName,
    resolvedIconSize,
    rootClassName,
  } = useActionButtonDropdownMenu({
    classNameDropdown,
    closeOnClickInOption,
    customDropdown,
    emitClickEventOpen,
    fixed,
    icon,
    iconClose,
    iconSize,
    onChangeOpen,
    open,
    rotate,
    size,
  });

  return (
    <div className={rootClassName}>
      <div className={buttonClassName} ref={containerButtonRef}>
        <Button ariaExpanded={isOpen} ariaHasPopup={ariaHasPopup} ariaLabel={ariaLabel} iconSize={resolvedIconSize} isIconOnly leftIcon={iconButton} onClick={onToggleDropdown} />
      </div>
      {isOpen && (
        <Popover anchorEl={anchorEl} className={popoverClassName} handleClose={onCloseDropdown} open={isOpen} placement={placementDropdown}>
          {customDropdown ?? <ListSimpleDropdown colorIcon={iconColor} items={options} onClick={onClickInOption} />}
        </Popover>
      )}
    </div>
  );
}

export default ActionButtonDropdownMenu;
